/**
 * Als App installieren (PWA): Chrome und Edge melden `beforeinstallprompt` einmal beim
 * Laden, deshalb hält dieses Modul das Ereignis ab dem Start fest (Import in main.tsx).
 * Safari kennt kein Ereignis; dort bleibt nur der Hinweis auf „Zum Home-Bildschirm".
 */
export type InstallPlatform = 'installed' | 'ios' | 'android' | 'desktop';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as InstallPromptEvent;
  notify();
});
window.addEventListener('appinstalled', () => {
  deferred = null;
  notify();
});

export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function installPlatform(): InstallPlatform {
  if (matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone) return 'installed';
  if (isIos()) return 'ios';
  return /Android/.test(navigator.userAgent) ? 'android' : 'desktop';
}

export const canPrompt = () => deferred != null;

export function onInstallChange(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Zeigt den Installationsdialog des Browsers; true, wenn installiert wurde. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  notify();
  return outcome === 'accepted';
}
